function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { createRef, useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from '@guryou/html-components';
import { addImageInProviderGallery, resetState, stateIsLoaded, stateIsLoading, UPLOAD_FILE, uploadFileToS3 } from '@guryou/redux';
import Slider from '@material-ui/core/Slider';
import { useTheme } from '@material-ui/core/styles';
import { ContainedButton, IconButton } from '../buttons';
import { Block, DefaultText, FlexRow, Title } from '../content';
import { DeleteIcon } from '../icons';
import CropFormatPicker, { CROP_FORMATS, cropSizeStatus, MIN_LONG_SIDE, RECOMMENDED_LONG_SIDE } from './CropFormatPicker';
const defaultState = {
  crop: {
    x: 0,
    y: 0
  },
  zoom: 1,
  selectedUrl: null,
  croppedInfo: null,
  file: null
};
const CropImage = ({
  onSuccess,
  formats = CROP_FORMATS,
  minLongSide = MIN_LONG_SIDE,
  recommendedLongSide = RECOMMENDED_LONG_SIDE
}) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [ls, setLs] = useState(defaultState);
  const [formatId, setFormatId] = useState(formats[0].id);
  const aspect = (formats.find(f => f.id === formatId) || formats[0]).aspect;
  const sizeStatus = cropSizeStatus(ls.croppedInfo, minLongSide, recommendedLongSide);
  const addImage = useSelector(state => state.provider.addProviderImage);
  const uploadImage = useSelector(state => state.files.upload);
  const inputFileRef = /*#__PURE__*/createRef();
  const onSuccessCallback = useCallback(() => {
    onSuccess && onSuccess(ls.selectedUrl);
  }, [ls.selectedUrl, onSuccess]);
  useEffect(() => {
    if (stateIsLoaded(uploadImage)) {
      dispatch(resetState(UPLOAD_FILE));
      onSuccessCallback();
      setLs(defaultState);
    }
  }, [dispatch, ls.selectedUrl, onSuccessCallback, uploadImage, uploadImage.version]);
  useEffect(() => {
    if (ls.file && stateIsLoaded(addImage) && addImage.data?.presignedUrl) {
      dispatch(uploadFileToS3(ls.file, addImage.data?.presignedUrl));
    }
    //eslint-disable-next-line
  }, [addImage.version, dispatch]);
  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setLs(ps => ({
      ...ps,
      croppedInfo: croppedAreaPixels
    }));
  }, []);
  const onCropChange = crop => {
    setLs(ps => ({
      ...ps,
      crop
    }));
  };
  const onZoomChange = zoom => {
    setLs(ps => ({
      ...ps,
      zoom
    }));
  };
  const removeSelectedUrl = useCallback(() => {
    setLs(ps => ({
      ...ps,
      selectedUrl: '',
      crop: {
        x: 0,
        y: 0
      },
      croppedInfo: ''
    }));
  }, []);
  const handleInsertImage = () => {
    if (sizeStatus === 'tooSmall') {
      return;
    } else {
      const metadata = {
        x: ls.croppedInfo.x.toString(),
        y: ls.croppedInfo.y.toString(),
        width: ls.croppedInfo.width.toString(),
        height: ls.croppedInfo.height.toString()
      };
      const extension = ls.file.name.split('.').pop();
      const name = `file_${+Date.now()}.${extension}`;
      dispatch(addImageInProviderGallery(name, metadata));
    }
  };
  const onSelectFile = e => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setLs(ps => ({
          ...ps,
          selectedUrl: reader.result
        }));
      });
      reader.readAsDataURL(e.target.files[0]);
      setLs(ps => ({
        ...ps,
        file: e.target.files[0]
      }));
    }
  };
  const controlsPosition = ls.selectedUrl ? {
    bottom: 0
  } : {
    top: 0
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
    hidden: true,
    id: "cropImage",
    type: "file",
    accept: "image/*",
    onChange: onSelectFile,
    ref: inputFileRef
  }), ls.croppedInfo && /*#__PURE__*/React.createElement(FlexRow, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Title, {
    textAlign: "center",
    xs: 6,
    title: ls.croppedInfo?.width,
    description: t('PROVIDER-GALLERY.width'),
    whiteSpace: "nowrap"
  }), /*#__PURE__*/React.createElement(Title, {
    textAlign: "center",
    xs: 6,
    title: ls.croppedInfo?.height,
    description: t('PROVIDER-GALLERY.height'),
    whiteSpace: "nowrap"
  }), sizeStatus === 'tooSmall' && /*#__PURE__*/React.createElement(DefaultText, {
    color: theme.palette.error.main
  }, t('VALIDATIONS.imageTooSmall')), sizeStatus === 'small' && /*#__PURE__*/React.createElement(DefaultText, {
    color: theme.palette.warning.main
  }, t('PROVIDER-GALLERY.imageBelowRecommended', 'Small image: it may look blurry on large screens.'))), stateIsLoading(addImage) || stateIsLoading(uploadImage) ? /*#__PURE__*/React.createElement(Spinner, null) : /*#__PURE__*/React.createElement(Block, {
    position: "relative",
    height: formats.length > 1 ? '480px' : '400px'
  }, ls.selectedUrl && /*#__PURE__*/React.createElement(Block, {
    margin: 0,
    position: "absolute",
    height: "300px",
    top: 0
  }, /*#__PURE__*/React.createElement(Cropper, {
    image: ls.selectedUrl,
    crop: ls.crop,
    zoom: ls.zoom,
    aspect: aspect,
    onCropChange: onCropChange,
    onCropComplete: onCropComplete,
    onZoomChange: onZoomChange
  })), /*#__PURE__*/React.createElement(Block, _extends({
    margin: 0,
    position: "absolute",
    textAlign: "center"
  }, controlsPosition), !ls.selectedUrl && /*#__PURE__*/React.createElement(ContainedButton, {
    color: "primary",
    onClick: () => inputFileRef.current.click(),
    text: t('PROVIDER-GALLERY.+image')
  }), ls.selectedUrl && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CropFormatPicker, {
    formats: formats,
    value: formatId,
    onChange: setFormatId
  }), /*#__PURE__*/React.createElement(Block, {
    xs: 6,
    padding: 5,
    margin: "auto"
  }, /*#__PURE__*/React.createElement(Slider, {
    value: ls.zoom,
    min: 1,
    max: 3,
    step: 0.1,
    "aria-labelledby": "Zoom",
    onChange: (_, zoom) => setLs(ps => ({
      ...ps,
      zoom
    }))
  })), /*#__PURE__*/React.createElement(FlexRow, {
    margin: "auto",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(ContainedButton, {
    disabled: sizeStatus === 'tooSmall',
    color: "primary",
    onClick: handleInsertImage,
    text: t('PROVIDER-GALLERY.insertImage')
  }), /*#__PURE__*/React.createElement(IconButton, {
    icon: DeleteIcon,
    color: "secondary",
    onClick: removeSelectedUrl
  }))))));
};
export default CropImage;